import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, ACCOUNT_COLORS } from "@/lib/api";

const KEYS = ["1","2","3","4","5","6","7","8","9","","0","DEL"];

export default function PinModal({ user, open, onClose, onSuccess }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [remember, setRemember] = useState(true);

  useEffect(() => {
    if (open) {
      setPin("");
      setError("");
    }
  }, [open]);

  const tap = (k) => {
    setError("");
    if (k === "DEL") {
      setPin((p) => p.slice(0, -1));
      return;
    }
    if (!k) return;
    setPin((p) => (p.length < 4 ? p + k : p));
  };

  const submit = async (entered) => {
    if (!user) return;
    setBusy(true);
    try {
      const { ok } = await api.verifyPin(user.id, entered);
      if (ok) {
        onSuccess(user, { remember });
      } else {
        setError("INCORRECT PIN");
        setPin("");
      }
    } catch (err) {
      // Surface the REAL backend error so production blips are diagnosable.
      // Common production cause: backend missing JWT_SECRET env var → 500
      // "Auth not configured" from _issue_token (server.py). We log the full
      // axios error and show a status-code-aware label so the user (and any
      // dev watching the console) can tell network/CORS/500 apart from a
      // simple wrong PIN.
      // eslint-disable-next-line no-console
      console.error("[PinModal] verify-pin failed:", err?.response?.status, err?.response?.data, err);
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let label;
      if (!err?.response) label = "NETWORK ERROR — CHECK CONNECTION";
      else if (status === 500 && /JWT|auth not configured/i.test(detail || "")) label = "SERVER MISCONFIG — JWT_SECRET MISSING";
      else if (status === 500) label = `SERVER ERROR 500${detail ? " — " + String(detail).toUpperCase() : ""}`;
      else if (status === 401 || status === 403) label = "UNAUTHORIZED";
      else if (status === 404) label = "PROFILE NOT FOUND";
      else label = `ERROR${status ? " " + status : ""} — TRY AGAIN`;
      setError(label);
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (pin.length === 4) submit(pin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  if (!user) return null;
  const color = ACCOUNT_COLORS[user.account_type] || "#fff";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-md bg-[var(--stakes-surface)] border-[var(--stakes-border)] rounded-none p-0 [&>button]:hidden"
        data-testid="pin-modal"
      >
        <DialogTitle className="sr-only">Enter PIN for {user.name}</DialogTitle>
        <DialogDescription className="sr-only">Enter 4-digit PIN</DialogDescription>
        <div className="p-8 border-b border-[var(--stakes-border)]">
          <div className="label-mono mb-2" style={{ color }}>RESTRICTED PROFILE</div>
          <div className="font-anton text-5xl uppercase leading-none">{user.name}</div>
          <div className="label-mono mt-3">ENTER 4-DIGIT PIN</div>

          <div className="flex gap-3 mt-6" data-testid="pin-display">
            {[0,1,2,3].map((i) => (
              <div
                key={i}
                className="flex-1 h-16 border-2 flex items-center justify-center font-anton text-4xl"
                style={{
                  borderColor: i < pin.length ? color : "var(--stakes-border)",
                  background: i < pin.length ? "rgba(255,255,255,0.04)" : "transparent",
                }}
              >
                {i < pin.length ? "●" : ""}
              </div>
            ))}
          </div>
          {error && (
            <div className="mt-4 label-mono" style={{ color: "var(--stakes-primary)" }} data-testid="pin-error">
              {error}
            </div>
          )}
        </div>
        <div className="grid grid-cols-3 bg-[var(--stakes-border)] gap-px">
          {KEYS.map((k, idx) => (
            <button
              key={idx}
              disabled={busy || !k}
              onClick={() => tap(k)}
              data-testid={k ? `pin-numpad-${k.toLowerCase()}` : `pin-numpad-empty-${idx}`}
              className={`h-20 bg-[var(--stakes-surface)] font-barlow font-bold text-3xl uppercase tracking-wider hover-flip ${
                k && k !== "DEL"
                  ? "hover:bg-white hover:text-black"
                  : k === "DEL"
                    ? "hover:bg-[var(--stakes-primary)]"
                    : "cursor-default"
              }`}
            >
              {k}
            </button>
          ))}
        </div>
        <div className="p-4 border-t border-[var(--stakes-border)] flex items-center justify-between flex-wrap gap-2">
          <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="pin-remember-label">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="w-4 h-4 accent-[var(--neon-blue)]"
              data-testid="pin-remember-checkbox"
            />
            <span className="label-mono">REMEMBER ME ON THIS DEVICE</span>
          </label>
          <button
            onClick={onClose}
            className="label-mono hover:text-white"
            data-testid="pin-cancel-button"
          >
            ← CANCEL
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
